import { useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { useAccount } from '@/contexts/AccountContext'
import type { CollectionConfig, FieldConfig } from '../config/collectionRegistry'

interface CollectionEntryModalProps {
  config: CollectionConfig
  existing?: Record<string, unknown>
  isSaving: boolean
  error?: string | null
  onClose: () => void
  onSubmit: (values: Record<string, unknown>) => void
}

export function CollectionEntryModal({
  config,
  existing,
  isSaving,
  error,
  onClose,
  onSubmit,
}: CollectionEntryModalProps) {
  const isEdit = !!existing
  const { selectedAccountId } = useAccount()

  const [values, setValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {}
    for (const field of config.fields) {
      const raw = existing?.[field.key]
      initial[field.key] = raw != null && raw !== '' ? String(raw) : ''
    }
    return initial
  })

  const set = (key: string, val: string) => setValues((prev) => ({ ...prev, [key]: val }))

  const isVisible = (field: FieldConfig) => {
    if (!field.dependsOn) return true
    return values[field.dependsOn.field] === field.dependsOn.value
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const payload: Record<string, unknown> = {}
    for (const field of config.fields) {
      const val = values[field.key]
      // Hidden conditional fields send null so the backend clears them
      if (!isVisible(field)) {
        payload[field.key] = null
        continue
      }
      payload[field.key] = val === '' ? null : val
    }
    onSubmit(payload)
  }

  // Group fields by section, preserving order
  const sections = (() => {
    const map = new Map<string, FieldConfig[]>()
    for (const field of config.fields) {
      const key = field.section ?? ''
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(field)
    }
    return Array.from(map.entries()) // [[sectionName, fields[]], ...]
  })()

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {isEdit ? 'Edit Token' : 'New Token'}
            </h2>
            {selectedAccountId && selectedAccountId !== 'ALL' && (
              <p className="text-xs text-gray-400 mt-0.5">
                Account&nbsp;<span className="font-mono font-medium text-gray-600">{selectedAccountId}</span>
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 px-6 py-5 space-y-6">
          {sections.map(([sectionName, fields]) => {
            const visibleFields = fields.filter(isVisible)
            if (visibleFields.length === 0) return null

            return (
              <div key={sectionName}>
                {sectionName && (
                  <div className="flex items-center gap-3 mb-3">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      {sectionName}
                    </p>
                    <div className="flex-1 h-px bg-gray-100" />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-x-4 gap-y-4">
                  {visibleFields.map((field) => (
                    <div key={field.key} className={field.fullWidth ? 'col-span-2' : ''}>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        {field.label}
                        {field.required && <span className="text-red-500 ml-0.5">*</span>}
                      </label>

                      {field.fieldType === 'select' ? (
                        <Select
                          value={values[field.key]}
                          onChange={(e) => set(field.key, e.target.value)}
                          options={field.options ?? []}
                          placeholder={`Select…`}
                          required={field.required}
                        />
                      ) : (
                        <Input
                          value={values[field.key]}
                          onChange={(e) => set(field.key, e.target.value)}
                          placeholder={field.placeholder}
                          required={field.required}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )
          })}

          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-xl shrink-0">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="collection-entry-form" disabled={isSaving} onClick={handleSubmit}>
            {isSaving ? 'Saving…' : isEdit ? 'Update Token' : 'Create Token'}
          </Button>
        </div>
      </div>
    </div>
  )
}
